import styled, { css } from 'styled-components';

import Clickable from '@polyai/ui/components/atoms/Clickable';
import IconButton from '@polyai/ui/components/atoms/IconButton';
import Input from '@polyai/ui/components/atoms/Input';
import {
  Body,
  BodyStrong,
  Caption,
  InputText,
} from '@polyai/ui/components/atoms/LegacyText';
import Select from '@polyai/ui/components/atoms/Select';
import Text from '@polyai/ui/components/atoms/Text';
import { CodeInput } from '@polyai/ui/components/molecules/CodeInput';
import themeColors from '@polyai/ui/styles/config/colors';
import themeSpacing from '@polyai/ui/styles/config/spacing';
import text from '@polyai/ui/styles/config/text';
import rem from '@polyai/ui/styles/functions/rem';

// ParameterInput

export const InfiniteInputRowWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${themeSpacing.xs2};
  align-items: start;

  width: 100%;
  max-width: 100%;
`;

export const TypeInput = styled.div<{ $stretch: boolean }>`
  grid-column: span ${({ $stretch }) => ($stretch ? 2 : 1)};
`;

export const InfiniteInput = styled.div<{ $span?: number }>`
  grid-column: span ${({ $span }) => $span || 1};
`;

export const DeleteContainer = styled(InfiniteInput)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 45px; /* Need a better solution */
`;

export const TypeSelect = styled(Select)`
  * {
    font-family: ${text.font.monospace};
    font-size: ${text.size.body.small};
    font-weight: ${text.weight.regular};
  }
` as typeof Select;

// FunctionForm

export const FunctionInput = styled(Input)`
  background-color: ${themeColors.background};
`;

export const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs1};
`;

export const ToggleWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;

  width: 100%;
  max-width: ${rem(180)};
  height: 40px;
  margin-inline-start: auto;

  ${({ $compact }) =>
    $compact &&
    css`
      margin-inline-start: 0;
    `}
`;

export const ToggleLabel = styled(Caption)<{ $disabled?: boolean }>`
  margin-left: ${themeSpacing.xs3};
  font-size: ${text.size.body.regular};
  color: ${themeColors.textSecondary};
`;

export const ParametersInput = styled.div<{ $isCondensed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isCondensed }) =>
    $isCondensed ? themeSpacing.sm4 : themeSpacing.xs2};

  width: 100%;
  max-width: 100%;
`;

const labelCss = css`
  font-size: ${text.size.body.small};
  color: ${themeColors.textSecondary};
`;

export const InputLabel = styled.div`
  ${labelCss}
`;

export const CodeLabel = styled(InputLabel)`
  margin-bottom: ${themeSpacing.xs2};
`;

export const InfiniteInputLabel = styled(InputLabel)<{ $span?: number }>`
  grid-column: span ${({ $span }) => $span || 1};
  margin-bottom: ${themeSpacing.xs1};
`;

export const ParametersTitle = styled(InputText)`
  margin-bottom: ${themeSpacing.sm1};
  color: ${themeColors.textSecondary};
`;

export const LabelRow = styled(InfiniteInputRowWrapper)`
  margin-bottom: ${themeSpacing.xs2};
`;

export const ParametersErrorWrapper = styled.div`
  margin: ${themeSpacing.xs2} 0;
`;

export const LibrariesLink = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  text-decoration: underline;
`;

// CodeInput

export const CustomCodeInput = styled(CodeInput)`
  &&& {
    /* stylelint-disable-next-line selector-class-pattern */
    .cm-editor {
      background-color: ${themeColors.background};
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .cm-lineNumbers {
      width: 32px;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .cm-focused {
      border: 1px solid ${themeColors.borderStrong};
      border-radius: ${themeSpacing.radiusSmall};
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .cm-placeholder {
      white-space: pre;
    }
  }
`;

export const TopCodeInput = styled(CustomCodeInput)`
  &&& {
    /* stylelint-disable-next-line selector-class-pattern */
    .cm-editor {
      border-bottom: none;
      border-radius: ${themeSpacing.radiusSmall} ${themeSpacing.radiusSmall} 0 0;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .cm-content {
      padding: 16px 8px 0;
    }
  }
`;

export const BottomCodeInput = styled(CustomCodeInput)`
  &&& {
    /* stylelint-disable-next-line selector-class-pattern */
    .cm-editor {
      border-top: none;
      border-radius: 0 0 ${themeSpacing.radiusSmall} ${themeSpacing.radiusSmall};
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .cm-content {
      padding: 0 8px 16px;
    }
  }
`;

export const FunctionDefinitionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
`;

export const SecretsButton = styled(IconButton)`
  gap: ${themeSpacing.xs1};
`;

export const SecretsModalSubtext = styled(Body)`
  margin-top: ${themeSpacing.xs1};
  color: ${themeColors.textSecondary};
`;

export const NoneAvailableText = styled(BodyStrong)`
  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;
  color: ${themeColors.textDisabled};
`;

export const AvailableSecretsWrapper = styled.div`
  margin-top: ${themeSpacing.sm4};
  margin-bottom: ${themeSpacing.xs2};
`;

const SECRETS_WRAPPER_MAX_HEIGHT = 215;
const SECRETS_BOTTOM_BORDER_HEIGHT = 1;

export const SecretCardsWrapper = styled.div<{ $currentHeight: number }>`
  position: relative;
  overflow-y: auto;
  max-height: ${SECRETS_WRAPPER_MAX_HEIGHT}px;
  border-bottom: ${SECRETS_BOTTOM_BORDER_HEIGHT}px solid
    ${themeColors.borderUnderline};

  ${({ $currentHeight }) =>
    // bottom border will reduce the client height so accounting for it
    $currentHeight >=
      SECRETS_WRAPPER_MAX_HEIGHT - SECRETS_BOTTOM_BORDER_HEIGHT &&
    css`
      box-shadow: inset 0 -25px 25px -25px ${themeColors.shadow};
    `}

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SecretCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${themeSpacing.xs3} 0;
`;

export const ValueTypeText = styled(Caption)`
  color: ${themeColors.textSecondary};
`;

export const LearnMoreLink = styled.a`
  font-weight: ${text.weight.semibold};
`;

export const Description = styled(Body)`
  margin: ${themeSpacing.sm1} 0;
  color: ${themeColors.textSecondary};
`;

export const SidebarTitleWrapper = styled.div`
  position: sticky;

  display: flex;
  justify-content: space-between;

  margin-bottom: ${themeSpacing.xs1};
  padding: ${themeSpacing.sm3};
`;

export const RightButtons = styled.div`
  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;
`;

// DelayControl

export const SlidersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.sm1};
  width: 45%;
`;

export const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xxs2};
`;

export const MetaDataWrapper = styled.div<{ $compact?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: ${themeSpacing.sm1};

  ${({ $compact }) =>
    $compact &&
    css`
      flex-direction: column;
    `}
`;

export const TimeoutCounterWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  gap: ${themeSpacing.xxs2};
  align-items: start;

  ${({ $compact }) =>
    !$compact &&
    css`
      justify-content: flex-end;
      min-width: 120px;
    `}
`;

export const TimeoutLabel = styled(InputLabel)<{ $compact?: boolean }>`
  ${({ $compact }) =>
    !$compact &&
    css`
      display: flex;
      justify-content: flex-end;
    `}
`;

export const TimeoutCounterTextWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const LabelWrapper = styled.span<{ $compact?: boolean }>`
  text-align: ${({ $compact }) => ($compact ? 'right' : 'left')};
`;

export const SecondWrapper = styled.span<{ $compact?: boolean }>`
  display: flex;
  gap: ${themeSpacing.xs1};
  align-items: center;

  min-width: 0;

  text-align: ${({ $compact }) => ($compact ? 'right' : 'left')};
`;

export const DelayResponsesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs1};
`;

export const Hint = styled(Body)`
  color: ${themeColors.textPlaceholder};
`;

export const DelayDescriptionText = styled(Text)`
  color: ${themeColors.textSecondary};
`;

export const DurationInputWrapper = styled.div`
  min-width: ${rem(88)};
`;
